import { useLocation, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { warmAuthenticatedRouteGraph } from '@/lib/pwa/route-graph-warm';
import {
  getLikelyNextWarmTargets,
  shouldSkipPrimaryRouteWarming,
  warmPrimaryRoutes,
  type WarmStartConnection,
} from '@/lib/pwa/warm-start';

function getWarmStartConnection(): WarmStartConnection {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string; rtt?: number; downlink?: number };
    }
  ).connection as
    { saveData?: boolean; effectiveType?: string; rtt?: number; downlink?: number } | undefined;

  return {
    online: navigator.onLine,
    saveData: connection?.saveData,
    effectiveType: connection?.effectiveType,
    rtt: connection?.rtt,
    downlink: connection?.downlink,
  };
}

type IdleHandle = number;
type IdleCallback = (deadline: { didTimeout: boolean; timeRemaining: () => number }) => void;

// Run warming off the critical path so it never gates first paint (#415). The
// requestIdleCallback fallback covers older Safari where it is unavailable.
function scheduleWhenIdle(task: () => void): () => void {
  const requestIdle = (
    window as unknown as {
      requestIdleCallback?: (cb: IdleCallback, opts?: { timeout: number }) => IdleHandle;
      cancelIdleCallback?: (handle: IdleHandle) => void;
    }
  ).requestIdleCallback;

  if (requestIdle) {
    const handle = requestIdle(task, { timeout: 2000 });
    return () =>
      (
        window as unknown as { cancelIdleCallback?: (handle: IdleHandle) => void }
      ).cancelIdleCallback?.(handle);
  }

  const handle = window.setTimeout(task, 0);
  return () => window.clearTimeout(handle);
}

/**
 * Non-blocking route warmer for authenticated users (#415). Renders nothing so the
 * requested authenticated route paints immediately; the likely-next primary routes
 * are preloaded once, after first paint, on an idle callback. Network/saveData
 * conditions are still respected via shouldSkipPrimaryRouteWarming.
 */
export function AuthenticatedWarmStart() {
  const { authState } = useAuth();
  const router = useRouter();
  const location = useLocation();
  const [connection] = useState(getWarmStartConnection);
  const warmedRef = useRef(false);

  useEffect(() => {
    if (warmedRef.current) return;
    if (authState !== 'authenticated') return;
    if (shouldSkipPrimaryRouteWarming(connection)) return;
    warmedRef.current = true;

    const activePathname = location.pathname;
    const cancel = scheduleWhenIdle(() => {
      void warmPrimaryRoutes(router, getLikelyNextWarmTargets(activePathname))
        .catch(() => undefined)
        // Then the rest of the authed route graph, code only: the two tab roots
        // above are warm already, so a tap into any subpage (profile, feature
        // requests, post detail) no longer pays a chunk download plus parse
        // while the user waits on the destination skeleton.
        .then(() => warmAuthenticatedRouteGraph());
    });
    return cancel;
    // Warm once when authentication is established; the router already preloads
    // on hover/focus intent (defaultPreload) for subsequent navigations.
  }, [authState, connection, router, location.pathname]);

  return null;
}
